import { storableError } from '../../util/errors';
import { skillFitTranscript } from '../../util/api';

/**
 * `SkillFitTranscriptPage` state — the interview record a reviewer reads at
 * `/skillfit-transcript/:candidateListingId/:jobListingId`.
 *
 * ⭐ **Screen 10's report duck, verbatim in shape**. That is deliberate: the two
 * surfaces are gated by the SAME server-side rule, reached by the SAME two ids, and refuse in the
 * SAME two ways — so they load in the same way too, and a reader of one already knows the other.
 *
 * ⛔ **No interview id and no report id.** The endpoint resolves the interview off the gated entry
 * (`entry.aiInterviewId`); a caller-supplied id would be a second resolution path and a second thing
 * to gate.
 *
 * ⚠ The two listing ids are load-bearing TOGETHER: a SkillFit Check belongs to a job application,
 * not to a person. Dropping `jobListingId` serves one application's interview under
 * another's address.
 */

// ================ Action types ================ //

export const FETCH_TRANSCRIPT_REQUEST = 'app/SkillFitTranscriptPage/FETCH_TRANSCRIPT_REQUEST';
export const FETCH_TRANSCRIPT_SUCCESS = 'app/SkillFitTranscriptPage/FETCH_TRANSCRIPT_SUCCESS';
export const FETCH_TRANSCRIPT_ERROR = 'app/SkillFitTranscriptPage/FETCH_TRANSCRIPT_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchInProgress: false,
  fetchError: null,
  transcript: null,
};

export default function skillFitTranscriptPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_TRANSCRIPT_REQUEST:
      // ⛔ The previous transcript is dropped on the way IN, not on the way out. Two candidates'
      // interviews are reached by two URLs on the same page, so a request that left the old record
      // standing would show one candidate's own words under another candidate's address while it
      // loads — and a transcript is the most quotable thing this product holds.
      return { ...state, fetchInProgress: true, fetchError: null, transcript: null };
    case FETCH_TRANSCRIPT_SUCCESS:
      // ⚠ Stored verbatim, ⛔ never reshaped here. An EMPTY record (`turnCount: 0`) is a record and
      // lands like any other — the "nothing was recorded" finding is a `ready` state, not a gap.
      return { ...state, fetchInProgress: false, transcript: payload };
    case FETCH_TRANSCRIPT_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchTranscriptRequest = () => ({ type: FETCH_TRANSCRIPT_REQUEST });
export const fetchTranscriptSuccess = payload => ({ type: FETCH_TRANSCRIPT_SUCCESS, payload });
export const fetchTranscriptError = e => ({
  type: FETCH_TRANSCRIPT_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

/**
 * ⛔ **Resolves even when the fetch fails, and never rethrows.** The refusals this endpoint issues
 * are the page's content, not a broken load: `404` is "no such completed check" and `403` is the org
 * gate declining to disclose that one exists at all — deliberately indistinguishable, and rendered
 * as ONE message. Rejecting here would fail the data-loading pass and hand the reviewer a blank
 * document instead of the server's answer.
 *
 * `storableError` keeps `status`, which is the only thing separating those two refusals from a
 * genuine failure — see the page's `resolveViewState`. ⛔ The duck itself classifies nothing.
 */
export const loadData = params => dispatch => {
  const candidateListingId = params && params.candidateListingId;
  const jobListingId = params && params.jobListingId;

  dispatch(fetchTranscriptRequest());
  return skillFitTranscript(candidateListingId, jobListingId)
    .then(response => {
      dispatch(fetchTranscriptSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchTranscriptError(storableError(e)));
    });
};
